import { memo, useCallback, useEffect, useMemo } from 'react'

import { CreatorPermission, type TeamMemberDto, TeamMemberRole } from '@api'
import { useUnit } from 'effector-react'
import mergeDeep from 'lodash/merge'
import omit from 'lodash/omit'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as z from 'zod'

import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx'
import { useToast } from '@/components/ui/toast'
import { $creators } from '@/models/creators.ts'
import { emailSchema } from '@/types/schemas.ts'
import { AppRoutes } from '@/utils/constants.ts'
import { handleError } from '@/utils/error'

import { useUpdateTeamTable } from '../../hooks/use-update-table'
import type {
  TeamMemberCreatorPermission,
  TeamMemberPermissionsTransformed,
  TeamRole,
} from '../../types'
import { TeamGeneralInfoForm } from '../../ui/members/edit/team-general-info-form'
import { TeamMemberPermissionsForm } from '../../ui/members/edit/team-member-permissions'
import type { PermissionsTableFormFields } from '../../ui/permissions-table-form'

export type TeamMemberEditFormValues = z.infer<
  typeof teamMemberEditFormSchema
> &
  PermissionsTableFormFields

const teamMemberEditFormSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: emailSchema,
  role: z.nativeEnum(TeamMemberRole),
  roleIds: z.array(z.number()),
  nsfwAlwaysOn: z.boolean(),
})

export const TeamEditForm = memo(
  ({
    data,
    permissions: memberPermissions,
    roles,
  }: {
    data: TeamMemberDto
    roles: TeamRole[]
    permissions: TeamMemberPermissionsTransformed
  }) => {
    const navigate = useNavigate()
    const creators = useUnit($creators)
    const toast = useToast()
    const redirect = useNavigate()

    const { updateMember } = useUpdateTeamTable()

    const getDefaultSelectedPermissions = useCallback(
      (
        memberPermissions: TeamMemberPermissionsTransformed,
        memberRoles: TeamRole[],
      ) => {
        return (
          memberPermissions.permissions?.map(
            ({ creatorId, creatorPermissions }) => {
              const permissionsReasonList = Object.keys(
                creatorPermissions,
              ).reduce<Partial<Record<CreatorPermission, string[]>>>(
                (acc, permission) => {
                  const selectedInRoles = memberRoles.filter((role) =>
                    role.permissions.some(
                      (rolePermission) =>
                        (rolePermission.permission === permission &&
                          rolePermission.creatorId === creatorId) ||
                        (rolePermission.permission === permission &&
                          rolePermission.assignToAllCreators),
                    ),
                  )

                  if (selectedInRoles.length === 0) return acc

                  acc[permission as CreatorPermission] = selectedInRoles.map(
                    (role) => role.name,
                  )

                  return acc
                },
                {},
              )

              return {
                creatorId,
                creatorName:
                  creators.find((creator) => creator.id === creatorId)?.name ||
                  'Unknown Creator',
                permissions: creatorPermissions,
                permissionsReasonList,
              }
            },
          ) || []
        )
      },
      [creators],
    )

    const defaultValues: TeamMemberEditFormValues = useMemo(() => {
      const memberRoles = roles.filter((role) =>
        data.roleIds?.includes(role.id),
      )

      return {
        id: data.id,
        name: data.name,
        email: data.email,
        role: data.role,
        roleIds:
          data.role === TeamMemberRole.ADMIN ||
          data.role === TeamMemberRole.LEADER
            ? []
            : data.roleIds || [],
        nsfwAlwaysOn: data.nsfwAlwaysOn,
        permissionsTable: {
          permissions: getDefaultSelectedPermissions(
            memberPermissions,
            memberRoles,
          ),
        },
      }
    }, [
      data.roleIds,
      data.email,
      data.id,
      data.name,
      data.role,
      data.nsfwAlwaysOn,
      getDefaultSelectedPermissions,
      memberPermissions,
      roles,
    ])
    const form = useForm<TeamMemberEditFormValues>({
      defaultValues,
    })

    const { watch } = form

    const roleIds = watch('roleIds')
    const role = watch('role')

    const handleBackNavigation = useCallback(() => {
      redirect(AppRoutes.Teams.Members.List.asUrl())
    }, [redirect])

    const handleSubmit = async ({
      permissionsTable,
      ...values
    }: TeamMemberEditFormValues) => {
      const transformedData: Partial<TeamMemberDto> = {
        ...values,
        // @ts-ignore
        permissions: memberPermissions.permissions.reduce(
          (acc, { creatorId, creatorPermissions }) => {
            const newCreatorPermissions = Object.keys(
              creatorPermissions,
            ).reduce<Partial<TeamMemberCreatorPermission>>(
              (acc, permissionKey) => {
                const creatorPermissions = permissionsTable.permissions.find(
                  (permission) => permission.creatorId === creatorId,
                )

                if (creatorPermissions === undefined) {
                  return acc
                }

                // Remove all OF permissions if the user does not have access to the OnlyFans profile
                if (
                  permissionKey.startsWith('OF_') &&
                  !(
                    creatorPermissions.permissions.ACCESS_ONLYFANS_PROFILE ||
                    creatorPermissions.permissionsReasonList
                      ?.ACCESS_ONLYFANS_PROFILE?.length
                  )
                ) {
                  acc[permissionKey as CreatorPermission] = false

                  return acc
                }

                acc[permissionKey as CreatorPermission] =
                  !!creatorPermissions.permissions[
                    permissionKey as CreatorPermission
                  ]

                return acc
              },
              {},
            )

            return { ...acc, [creatorId]: newCreatorPermissions }
          },
          {},
        ),
        userId: data.userId,
      }

      await updateMember
        .mutateAsync(transformedData)
        .then(() => {
          toast.success('Successfully updated user')
          navigate(AppRoutes.Teams.Members.List.asUrl())
        })
        .catch((error) => {
          handleError(
            error,
            'Something went wrong while updating the user. Try again later!',
          )
        })
    }

    useEffect(() => {
      const memberRoles = roles.filter((role) => roleIds.includes(role.id))

      const newPermissions = getDefaultSelectedPermissions(
        memberPermissions,
        memberRoles,
      )
      const currentPermissions = form.getValues('permissionsTable.permissions')
      const mergePermissions = currentPermissions.map((item) =>
        omit(item, 'permissionsReasonList'),
      )

      const allPermissions = mergeDeep(mergePermissions, newPermissions)

      form.setValue('permissionsTable.permissions', allPermissions)
    }, [form, getDefaultSelectedPermissions, memberPermissions, roleIds, roles])

    return (
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Page
            backToPage={{
              name: 'Team',
              url: AppRoutes.Teams.Members.List.asUrl(),
            }}
            title="Edit User"
            headerOptions={
              <>
                <Button
                  onClick={handleBackNavigation}
                  type="button"
                  variant="outline"
                  className="w-32"
                >
                  Cancel
                </Button>
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  type="submit"
                  variant="default"
                  className="w-32"
                  isLoading={updateMember.isLoading}
                >
                  Save
                </Button>
              </>
            }
          >
            <Card className="bg-card mb-8 rounded-lg p-8">
              <CardHeader className="p-0 pb-4">
                <CardTitle className="text-title text-lg font-semibold">
                  General Info
                </CardTitle>
              </CardHeader>
              <CardContent className="grid-flow-row p-0 sm:grid-flow-col">
                <TeamGeneralInfoForm
                  className="grid grid-cols-2 gap-4"
                  member={data}
                />
              </CardContent>
            </Card>
            <Card className="bg-card mb-8 rounded-lg p-8">
              <CardHeader className="p-0 pb-4">
                <CardTitle className="text-title text-lg font-semibold">
                  Permissions
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-flow-row gap-4 p-0 sm:grid-flow-col">
                <TeamMemberPermissionsForm role={role} />
              </CardContent>
            </Card>
          </Page>
        </form>
      </FormProvider>
    )
  },
)

TeamEditForm.displayName = 'TeamEditForm'
